import api from 'api';

// Storify / Raw / Publish and the Raws archive. These call the axios instance
// directly (not getReq/postReq) so the API's error message reaches the editor,
// e.g. "Write and save a headline and a story before publishing".

const unwrap = (res) => res.data.data;

export const newsroomError = (err, fallback) => {
    const message = err?.response?.data?.message;
    if (Array.isArray(message)) return message.join(' ');
    return typeof message === 'string' && message ? message : fallback;
};

export const fetchStory = async (alertId) =>
    unwrap(await api.get(`/alerts/${alertId}/story`));

export const saveStory = async (alertId, story) =>
    unwrap(await api.put(`/alerts/${alertId}/story`, story));

// Not fileUpload: its multipart interceptor stays on after a failed request. Axios sets
// the multipart header and boundary itself for FormData.
const formData = (fields) =>
    Object.entries(fields).reduce((form, [key, value]) => {
        form.append(key, value);
        return form;
    }, new FormData());

// The API checks the file and makes the 1200px copy and the crops; the story must be saved first.
export const uploadLeadImage = async (alertId, file, draft) =>
    unwrap(
        await api.post(
            `/alerts/${alertId}/story/lead-image`,
            formData({
                image: file,
                leadImageAlt: draft.leadImageAlt ?? '',
                leadImageCaption: draft.leadImageCaption ?? '',
                leadImageCredit: draft.leadImageCredit ?? '',
            })
        )
    );

export const publishStory = async (alertId) =>
    unwrap(await api.post(`/alerts/${alertId}/story/publish`));

export const unpublishStory = async (alertId) =>
    unwrap(await api.post(`/alerts/${alertId}/story/unpublish`));

// Out of Google (noindex,follow, off the sitemaps) or back in; the page stays live either way.
export const setAlertNoindex = async (alertId, noindex) =>
    unwrap(await api.put(`/alerts/${alertId}/noindex`, { noindex }));

export const markRaw = async (alertId, exclusiveUntil = null) =>
    unwrap(await api.post(`/alerts/${alertId}/raw`, { exclusiveUntil }));

// Back to the top of the Raws archive, without touching the alert's own dates.
export const markReraw = async (alertId) =>
    unwrap(await api.post(`/alerts/${alertId}/reraw`));

// "Not a Raw": out of the archive if it was in it, and out of the review queue.
export const markNotRaw = async (alertId) =>
    unwrap(await api.delete(`/alerts/${alertId}/raw`));

export const fetchRaws = async ({ q, tag, page = 1, size = 20 } = {}) => {
    const params = { page, size };
    if (q) params.q = q;
    if (tag) params.tags = tag;
    return unwrap(await api.get('/raws', { params }));
};

export const fetchRawQueue = async ({ page = 1, size = 20 } = {}) =>
    unwrap(await api.get('/raws/queue', { params: { page, size } }));

export const fetchNews = async ({
    page = 1,
    size = 20,
    profile,
    author,
} = {}) =>
    unwrap(await api.get('/news', { params: { page, size, profile, author } }));

export const fetchAuthors = async () => unwrap(await api.get('/authors'));
